/**
 * สลิปจ่ายนอกรอบของฉัน (ค่าคอม/OT) — พนักงานเห็นเฉพาะรอบที่บริษัทยืนยันจ่ายแล้ว.
 * ข้อมูลจาก GET /me/payouts (แยกจากสลิปเงินเดือนปกติ /slip).
 */
import { useState } from 'react';
import { Coins, Download } from 'lucide-react';
import { PageLayout } from '../components/layout/PageLayout';
import { useMyPayouts } from '../hooks';
import { apiFetch } from '../lib/api-client';
import { useToast } from '../components/ui/toast';

const fmt = (n: number) => n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const THAI_MONTHS_SHORT = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
const thaiDate = (s: string) => {
  const [y, m, d] = s.split('-').map(Number);
  return `${d} ${THAI_MONTHS_SHORT[(m ?? 1) - 1]} ${(y ?? 0) + 543}`;
};

export function MyPayouts() {
  const { data, isLoading } = useMyPayouts();
  const toast = useToast();
  const labelOf = new Map((data?.types ?? []).map((t) => [t.code, t.label]));
  const deductCodes = new Set((data?.types ?? []).filter((t) => t.kind === 'deduction').map((t) => t.code));
  const payouts = data?.payouts ?? [];

  const [pdfLoading, setPdfLoading] = useState<string | null>(null);
  async function downloadPdf(roundId: string) {
    if (pdfLoading) return;
    setPdfLoading(roundId);
    try {
      const { pdf_url } = await apiFetch<{ pdf_url: string }>(`/me/payouts/${roundId}/slip-pdf`);
      window.open(pdf_url, '_blank');
    } catch {
      toast.error('สร้าง PDF ไม่สำเร็จ ลองใหม่อีกครั้ง');
    } finally {
      setPdfLoading(null);
    }
  }

  return (
    <PageLayout title="สลิปเงินเดือน / ค่าคอม">
      <div className="space-y-3 pb-10">
        {isLoading ? (
          <p className="text-center text-gray-400 py-10 text-sm">กำลังโหลด…</p>
        ) : payouts.length === 0 ? (
          <div className="card p-8 text-center text-gray-400">
            <Coins className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p className="text-sm">ยังไม่มีรายการจ่ายนอกรอบ</p>
            <p className="text-[11px] mt-1">ค่าคอม/OT ที่บริษัทโอนแยกจากเงินเดือน จะแสดงที่นี่เมื่อบริษัทยืนยันจ่ายแล้ว</p>
          </div>
        ) : (
          payouts.map((p) => (
            <div key={p.round_id} className="card p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-medium text-gray-900">{p.title}</div>
                  <div className="text-xs text-gray-500 mt-0.5">จ่ายวันที่ {thaiDate(p.pay_date)}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-lg font-bold text-gray-900">฿{fmt(p.net)}</div>
                  <div className="text-[11px] text-gray-400">ยอดสุทธิ</div>
                </div>
              </div>
              <div className="mt-3 border-t border-gray-100 pt-2 space-y-1">
                {Object.entries(p.breakdown).map(([code, amount]) => {
                  const isDeduct = deductCodes.has(code);
                  return (
                    <div key={code} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">{labelOf.get(code) ?? code}</span>
                      <span className={isDeduct ? 'text-red-600' : 'text-gray-900'}>
                        {isDeduct ? `-${fmt(Number(amount))}` : fmt(Number(amount))}
                      </span>
                    </div>
                  );
                })}
                <div className="flex items-center justify-between text-sm pt-1 border-t border-gray-50">
                  <span className="text-gray-600 font-medium">รวมเงินเพิ่ม</span>
                  <span className="text-gray-900 font-medium">{fmt(p.gross)}</span>
                </div>
                {(p.deduct_total ?? 0) > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">รวมรายการหัก</span>
                    <span className="text-red-600">-{fmt(p.deduct_total)}</span>
                  </div>
                )}
                {p.wht_amount > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">หัก ณ ที่จ่าย {Number(p.wht_percent)}%</span>
                    <span className="text-red-600">-{fmt(p.wht_amount)}</span>
                  </div>
                )}
              </div>
              <button
                onClick={() => void downloadPdf(p.round_id)}
                disabled={pdfLoading === p.round_id}
                className="btn-secondary btn-slim w-full justify-center inline-flex items-center gap-1.5 mt-3"
              >
                <Download className="w-3.5 h-3.5" />
                {pdfLoading === p.round_id ? 'กำลังสร้าง PDF…' : 'ดาวน์โหลดสลิป PDF'}
              </button>
            </div>
          ))
        )}
        <p className="text-[11px] text-gray-400 text-center">
          สลิปนี้เป็นรายการที่บริษัทจ่ายแยกจากเงินเดือน — สลิปเงินเดือนปกติดูที่เมนู "สลิปเงินเดือน"
        </p>
      </div>
    </PageLayout>
  );
}
